import Image from "next/image";
import { cn, renderRichText } from "@/lib/utils";
import { type ColorScheme, COLOR_SCHEMES } from "@/lib/colorSchemes";

// ---------------------------------------------------------------------------
// ContentColumns — slot component for PageSection
// Renders 1–4 columns of stat/image + description pairs (e.g. "80+" / "94K")
// ---------------------------------------------------------------------------

export interface ContentColumnItem {
  /** Large stat or headline text (e.g. "80+", "4.1M"). */
  stat?: string;
  /** Optional image displayed above or instead of the stat. */
  imageSrc?: string;
  imageAlt?: string;
  /** Description text below the stat. */
  description?: string;
}

export interface ContentColumnsProps {
  /** The column items — typically 2–4. */
  items: ContentColumnItem[];
  /** Optional citation / source line displayed below the columns. */
  citation?: string;
  /** Color scheme — inherited from parent PageSection if not set. */
  colorScheme?: ColorScheme;
}

export function ContentColumns({
  items,
  citation,
  colorScheme,
}: ContentColumnsProps) {
  const scheme = colorScheme ? COLOR_SCHEMES[colorScheme] : undefined;
  const count = items.length;
  // Map item count to grid columns: 1→1, 2→2, 3→3, 4+→4
  const gridCols =
    count <= 1
      ? "grid-cols-1"
      : count === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : count === 3
          ? "grid-cols-1 sm:grid-cols-3"
          : "grid-cols-2 sm:grid-cols-4";

  return (
    <div>
      <div className={cn("grid gap-8", gridCols, scheme?.body)}>
        {items.map((item, i) => (
          <div key={i} className="text-center">
            {item.imageSrc && (
              <div className="flex justify-center">
                <Image
                  src={item.imageSrc}
                  alt={item.imageAlt ?? ""}
                  width={200}
                  height={200}
                  loading="eager"
                  className="object-contain max-h-[45px] w-auto"
                />
              </div>
            )}
            {item.stat && (
              <p
                className={cn(
                  "text-heading-lg font-semibold mt-4",
                  scheme?.heading,
                )}
              >
                {item.stat}
              </p>
            )}
            {item.description && (
              <p className={cn("text-body-sm mt-4", scheme?.body)}>
                {renderRichText(item.description)}
              </p>
            )}
          </div>
        ))}
      </div>
      {citation && (
        <p
          className={cn(
            "text-body-xs text-center mt-10 italic",
            scheme?.bodyMuted,
          )}
        >
          {citation}
        </p>
      )}
    </div>
  );
}
